import { Tag } from "~/components/Tag"
import type { ReactElement } from "react"
import { InfoCircleOutlined, RightOutlined } from "@ant-design/icons"
import { Button, Flex } from "antd"
import { Alert } from "~/components/Alert"
import {
  OrderType,
  PaymentMethod,
  POINT_ICON_URL,
  ProductCode,
} from "~/common/constants"
import { formatPrice } from "~/common/utils"
import { useAnalyticsEvent } from "~/components/Analytics"
import { CrownIcon, UserFilledIcon } from "~/components/Icons"
import { Text } from "~/components/Text"
import { useSessionStore } from "~/store/session"
import { PROFESSIONAL_PLAN } from "./professionalPlan"
import styles from "./index.css"

import {
  freePointUnitPrice,
  PolicyFooter,
  type PurchaseHandler,
} from "./shared"

type RechargePlan = {
  freeOriginalPrice: number
  points: number
  recommended?: boolean
}

const rechargePlans: RechargePlan[] = [
  { points: 100, freeOriginalPrice: 50 },
  { points: 300, freeOriginalPrice: 150, recommended: true },
  { points: 1000, freeOriginalPrice: 500 },
]

type RechargeContentProps = {
  unitPrice: number
  onPurchase: PurchaseHandler
}

type FreeRechargeContentProps = {
  unitPrice: number
  onViewProfessional: () => void
  onPurchase: PurchaseHandler
}

export const FreeRechargeContent = ({
  unitPrice,
  onViewProfessional,
  onPurchase,
}: FreeRechargeContentProps) => {
  return (
    <Flex
      className={styles.freeRechargeContent}
      vertical
      gap={12}
    >
      <Alert
        showIcon={false}
        style={{ padding: 16 }}
        description={
          <Flex
            justify="space-between"
            align="center"
          >
            <Flex
              align="center"
              gap={12}
            >
              <span className={styles.upgradeBannerIcon}>
                <CrownIcon />
              </span>
              <Flex vertical>
                <Text strong>开通专业版更划算</Text>
                <Text
                  color="primary"
                  size="s"
                >
                  ¥{PROFESSIONAL_PLAN.price}/月 含{PROFESSIONAL_PLAN.points}
                  积点+专业版权益，比单独充值{PROFESSIONAL_PLAN.points}积点 省
                  {PROFESSIONAL_PLAN.points * freePointUnitPrice -
                    PROFESSIONAL_PLAN.price}
                  元
                </Text>
              </Flex>
            </Flex>
            <Button
              icon={<RightOutlined />}
              iconPlacement="end"
              onClick={onViewProfessional}
              color="primary"
              variant="outlined"
              style={{ background: "transparent" }}
            >
              了解专业版
            </Button>
          </Flex>
        }
      />
      <RechargeContent
        getOriginalPrice={(plan) => plan.freeOriginalPrice}
        getOriginalPriceText={(plan) => `原价${plan.freeOriginalPrice}元`}
        noteItems={[
          "单独充值的积点永久有效，无过期时间，可随时使用",
          "当前免费版充值积点单价0.3元/积点；升级专业版可享充值会员专属价，单价低至0.25元/积点",
          "购买后积点将在10分钟内自动到账，可在积点明细中查看记录",
        ]}
        noteTitle="免费版用户充值说明"
        productCode={ProductCode.walletFreeV1}
        unitPrice={unitPrice}
        userPanel={<FreeUserPricePanel unitPrice={unitPrice} />}
        onPurchase={onPurchase}
      />
    </Flex>
  )
}

export const ProfessionalRechargeContent = ({
  unitPrice,
  onPurchase,
}: RechargeContentProps) => {
  return (
    <RechargeContent
      getOriginalPrice={(plan) => plan.points * freePointUnitPrice}
      getOriginalPriceText={(plan) =>
        `普通充值价${formatPrice(plan.points * freePointUnitPrice)}元`
      }
      noteItems={[
        "单独充值的积点永久有效，无过期时间，可随时使用",
        "专业版会员每月套餐包含300积点，日常消耗时会优先消耗会员套餐包含的积点，再消耗充值积点",
        "专业版用户专享0.25元/积点的优惠价格，比免费用户充值价便宜20%",
        "购买后积点将在10分钟内自动到账，可在积点明细中查看记录",
      ]}
      noteTitle="专业版用户充值说明"
      productCode={ProductCode.walletProV1}
      unitPrice={unitPrice}
      userPanel={<ProfessionalUserPricePanel unitPrice={unitPrice} />}
      onPurchase={onPurchase}
    />
  )
}

type RechargeContentSharedProps = {
  getOriginalPrice: (plan: RechargePlan) => number
  getOriginalPriceText: (plan: RechargePlan) => string
  noteItems: string[]
  noteTitle: string
  productCode: ProductCode
  unitPrice: number
  userPanel: ReactElement
  onPurchase: PurchaseHandler
}

const RechargeContent = ({
  getOriginalPrice,
  getOriginalPriceText,
  noteItems,
  noteTitle,
  productCode,
  unitPrice,
  userPanel,
  onPurchase,
}: RechargeContentSharedProps) => {
  return (
    <Flex
      className={styles.rechargeContent}
      vertical
      gap={12}
    >
      {userPanel}
      <Flex
        className={styles.rechargePlans}
        gap={12}
      >
        {rechargePlans.map((plan) => (
          <RechargeCard
            key={plan.points}
            originalPrice={getOriginalPrice(plan)}
            originalPriceText={getOriginalPriceText(plan)}
            plan={plan}
            productCode={productCode}
            unitPrice={unitPrice}
            onPurchase={onPurchase}
          />
        ))}
      </Flex>
      <Flex
        className={styles.rechargeNote}
        vertical
        gap={8}
      >
        <Flex
          align="center"
          gap={6}
        >
          <InfoCircleOutlined className={styles.noteIcon} />
          <Text color="secondary">{noteTitle}</Text>
        </Flex>
        <ul className={styles.noteList}>
          {noteItems.map((item) => (
            <li key={item}>
              <Text
                size={11}
                color="inherit"
              >
                {item}
              </Text>
            </li>
          ))}
        </ul>
      </Flex>
      <PolicyFooter />
    </Flex>
  )
}

type UserPricePanelProps = {
  unitPrice: number
}

const FreeUserPricePanel = ({ unitPrice }: UserPricePanelProps) => {
  return (
    <Flex
      className={styles.freeUserPricePanel}
      align="center"
      justify="space-between"
    >
      <Flex
        align="center"
        gap={6}
      >
        <UserFilledIcon className={styles.panelUserIcon} />
        <Text size="s">
          当前身份：
          <Text
            strong
            size="s"
          >
            免费版用户
          </Text>
        </Text>
      </Flex>
      <Text
        type="secondary"
        size="s"
      >
        当前充值积点单价：
        <Text
          color="primary"
          size="s"
        >
          {formatPrice(unitPrice)}元/积点
        </Text>
      </Text>
    </Flex>
  )
}

const ProfessionalUserPricePanel = ({ unitPrice }: UserPricePanelProps) => {
  const pointBalance = useSessionStore(
    (state) => state.user?.balance_info?.available_amount || 0,
  )

  return (
    <Flex
      className={styles.userPricePanel}
      align="center"
      justify="space-between"
    >
      <Flex
        align="center"
        gap={8}
      >
        <span className={styles.planIcon}>
          <CrownIcon />
        </span>
        <Flex
          vertical
          gap={4}
        >
          <Flex
            align="center"
            gap={8}
          >
            <Text strong>专业版会员</Text>
            <Tag color="primary">PRO</Tag>
          </Flex>
          <Text>有效期：以实际订阅为准</Text>
        </Flex>
      </Flex>
      <Flex
        align="flex-end"
        vertical
        gap={4}
      >
        <Text>
          当前剩余积点：
          <Text color="primary">{pointBalance}</Text>
        </Text>
        <Text>
          会员充值专享价：
          <Text
            size="l"
            color="primary"
          >
            {formatPrice(unitPrice)}元/积点
          </Text>
        </Text>
      </Flex>
    </Flex>
  )
}

type RechargeCardProps = {
  originalPrice: number
  originalPriceText: string
  plan: RechargePlan
  productCode: ProductCode
  unitPrice: number
  onPurchase: PurchaseHandler
}

const RechargeCard = ({
  originalPrice,
  originalPriceText,
  plan,
  productCode,
  unitPrice,
  onPurchase,
}: RechargeCardProps) => {
  const sendAnalytics = useAnalyticsEvent({
    category: "账户体系",
    prefix: "充值积分",
  })
  const price = plan.points * unitPrice

  return (
    <Flex
      className={
        plan.recommended ? styles.rechargeCardRecommended : styles.rechargeCard
      }
      align="center"
      vertical
    >
      {plan.recommended ? (
        <Text
          size={11}
          color="var(--ant-color-text-light-solid)"
          className={styles.rechargeRibbon}
        >
          推荐套餐
        </Text>
      ) : null}
      <Flex
        align="center"
        gap={8}
      >
        <img
          alt=""
          className={styles.pointIcon}
          src={POINT_ICON_URL}
        />
        <Text
          size="l"
          strong
        >
          {plan.points} 积点
        </Text>
      </Flex>
      <Text
        size="s"
        type="secondary"
      >
        永久有效
      </Text>
      <Text
        size={24}
        strong
        className={styles.rechargePrice}
        color={plan.recommended ? "primary" : "inherit"}
      >
        ¥{formatPrice(price)}
      </Text>
      <Text
        type="secondary"
        size="s"
        className={styles.originalPrice}
      >
        {originalPriceText}
      </Text>
      <Button
        block
        onClick={() => {
          sendAnalytics("点击充值")
          onPurchase({
            intent: {
              payment_method: PaymentMethod.wechat,
              order_type: OrderType.walletPackage,
              product_code: productCode,
              wallet_recharge_amount: plan.points,
            },
            originalAmount: Math.round(originalPrice * 100),
            successDescription: `${plan.points}积点已到账`,
            successTitle: "充值成功",
          })
        }}
        type={plan.recommended ? "primary" : "default"}
      >
        立即购买
      </Button>
    </Flex>
  )
}
